/**
 * emotion-effects.js
 * Tạo và quản lý các hiệu ứng biểu cảm Anime 3D phong cách Voxel (Emotion 3D FX)
 * Nổi lơ lửng trên đầu nhân vật: Giọt mồ hôi, Gân giận dữ, Dấu ?, Dấu !, Trái tim.
 */

import * as THREE from 'three';

export class EmotionEffectsManager {
  constructor() {
    this.activeEffects = new Map(); // avatarRoot -> { group, type, timer }
  }

  /**
   * Tạo mesh 3D voxel cho từng loại biểu cảm anime
   * @param {string} type - 'sweat' | 'anger' | 'question' | 'exclamation' | 'heart'
   * @returns {THREE.Group}
   */
  createEmotionMesh(type) {
    const group = new THREE.Group();
    group.name = `EmotionFX_${type}`;

    if (type === 'sweat') {
      // Giọt mồ hôi Anime (Màu xanh da trời, giọt nước hình bậc thang voxel)
      const mat = new THREE.MeshStandardMaterial({
        color: 0x38bdf8,
        roughness: 0.1,
        metalness: 0.1,
        emissive: 0x0284c7,
        emissiveIntensity: 0.3
      });

      // Thân giọt nước voxel
      const drop1 = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.14, 0.08), mat);
      const drop2 = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.16, 0.12), mat);
      drop2.position.y = -0.08;
      const drop3 = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.08, 0.1), mat);
      drop3.position.y = -0.18;

      group.add(drop1, drop2, drop3);
      group.position.set(0.28, 0.22, 0.1);
      group.rotation.z = -0.3; // Nghiêng nhẹ bên thái dương
    } else if (type === 'anger') {
      // Gân giận dữ Anime (💢 4 nhánh chéo chữ X màu đỏ tươi)
      const mat = new THREE.MeshStandardMaterial({
        color: 0xef4444,
        roughness: 0.3,
        emissive: 0xb91c1c,
        emissiveIntensity: 0.5
      });

      // Tạo 4 dấu cong góc vuông
      const size = 0.05;
      const offset = 0.08;
      const positions = [
        [-offset, offset],
        [offset, offset],
        [-offset, -offset],
        [offset, -offset]
      ];

      positions.forEach(([x, y]) => {
        const hBar = new THREE.Mesh(new THREE.BoxGeometry(0.08, size, 0.04), mat);
        hBar.position.set(x, y, 0);
        const vBar = new THREE.Mesh(new THREE.BoxGeometry(size, 0.08, 0.04), mat);
        vBar.position.set(x, y, 0);
        group.add(hBar, vBar);
      });

      group.position.set(0.3, 0.32, 0.1);
    } else if (type === 'question') {
      // Dấu hỏi chấm màu vàng tươi ❓
      const mat = new THREE.MeshStandardMaterial({
        color: 0xfacc15,
        roughness: 0.2,
        emissive: 0xca8a04,
        emissiveIntensity: 0.4
      });

      // Vòm cong trên
      const topBar = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.06, 0.06), mat);
      topBar.position.y = 0.22;

      const rightBar = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.1, 0.06), mat);
      rightBar.position.set(0.06, 0.15, 0);

      const midBar = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.06, 0.06), mat);
      midBar.position.set(0.03, 0.08, 0);

      const stalk = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.08, 0.06), mat);
      stalk.position.set(0, 0.02, 0);

      // Dấu chấm vuông dưới
      const dot = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.06, 0.06), mat);
      dot.position.set(0, -0.1, 0);

      group.add(topBar, rightBar, midBar, stalk, dot);
      group.position.set(0, 0.45, 0);
    } else if (type === 'exclamation') {
      // Dấu chấm than màu đỏ cam ❗
      const mat = new THREE.MeshStandardMaterial({
        color: 0xf97316,
        roughness: 0.2,
        emissive: 0xc2410c,
        emissiveIntensity: 0.5
      });

      // Thân côn trên
      const stem = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.22, 0.07), mat);
      stem.position.y = 0.08;

      // Dấu chấm dưới
      const dot = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.07, 0.07), mat);
      dot.position.y = -0.1;

      group.add(stem, dot);
      group.position.set(0, 0.45, 0);
    } else if (type === 'heart') {
      // Trái tim màu hồng ❤️
      const mat = new THREE.MeshStandardMaterial({
        color: 0xf43f5e,
        roughness: 0.2,
        emissive: 0xe11d48,
        emissiveIntensity: 0.4
      });

      const leftLobe = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.12, 0.08), mat);
      leftLobe.position.set(-0.06, 0.06, 0);

      const rightLobe = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.12, 0.08), mat);
      rightLobe.position.set(0.06, 0.06, 0);

      const bottom = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.12, 0.08), mat);
      bottom.position.set(0, -0.04, 0);

      const point = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.08, 0.08), mat);
      point.position.set(0, -0.12, 0);

      group.add(leftLobe, rightLobe, bottom, point);
      group.position.set(0, 0.45, 0);
    }

    return group;
  }

  /**
   * Gắn hiệu ứng biểu cảm lên đầu nhân vật
   * @param {BlockyAvatar} avatar
   * @param {string} emotionType - 'none' | 'sweat' | 'anger' | 'question' | 'exclamation' | 'heart'
   */
  attachEmotion(avatar, emotionType) {
    if (!avatar || !avatar.root) return;

    // Xóa hiệu ứng cũ nếu có
    this.removeEmotion(avatar);

    if (!emotionType || emotionType === 'none') return;

    const mesh = this.createEmotionMesh(emotionType);
    const targetParent = avatar.parts?.head || avatar.root;
    targetParent.add(mesh);

    this.activeEffects.set(avatar.root.uuid, {
      avatar,
      mesh,
      type: emotionType,
      timer: 0,
      baseY: mesh.position.y,
      baseScale: mesh.scale.clone()
    });
  }

  /**
   * Xóa bỏ hiệu ứng biểu cảm khỏi nhân vật
   * @param {BlockyAvatar} avatar
   */
  removeEmotion(avatar) {
    if (!avatar || !avatar.root) return;
    const item = this.activeEffects.get(avatar.root.uuid);
    if (item && item.mesh) {
      if (item.mesh.parent) {
        item.mesh.parent.remove(item.mesh);
      }
      this.activeEffects.delete(avatar.root.uuid);
    }
  }

  /**
   * Xóa toàn bộ hiệu ứng trên mọi nhân vật
   */
  clearAll() {
    this.activeEffects.forEach((item) => {
      if (item.mesh && item.mesh.parent) {
        item.mesh.parent.remove(item.mesh);
      }
    });
    this.activeEffects.clear();
  }

  /**
   * Cập nhật chuyển động hoạt họa cho các icon biểu cảm (nhấp nháy, nảy, phập phồng)
   * @param {number} delta
   */
  update(delta) {
    const d = Math.min(delta, 0.1);

    this.activeEffects.forEach((item) => {
      item.timer += d;
      const t = item.timer;
      const mesh = item.mesh;

      if (item.type === 'sweat') {
        // Nảy nhẹ và giọt mồ hôi trượt nhẹ xuống
        mesh.position.y = item.baseY - Math.sin(t * 6) * 0.02;
      } else if (item.type === 'anger') {
        // Gân giận co giãn xung nhịp (pulsing pulse)
        const scale = 1.0 + Math.sin(t * 10) * 0.15;
        mesh.scale.set(scale, scale, scale);
      } else if (item.type === 'question' || item.type === 'exclamation') {
        // Nhún nhảy lên xuống gây chú ý
        mesh.position.y = item.baseY + Math.sin(t * 5) * 0.04;
      } else if (item.type === 'heart') {
        // Nhịp đập trái tim (Thump-thump)
        const beat = 1.0 + Math.pow(Math.sin(t * 4), 6) * 0.25;
        mesh.scale.set(beat, beat, beat);
      }
    });
  }
}
